import { Phone, Video, FileText, Edit, FolderOpen, ChevronRight } from "lucide-react";
import SessionHistory from "./SessionHistory";
import SharedDocuments from "../SharedDocuments";
import {
  getModeIcon,
  getRiskColor,
  getStatusColor,
  getStatusText,
} from "../../utils";
import { useDispatch, useSelector } from "react-redux";
import { uploadFile } from "../../redux/api/Upload/Image";
import { endCallApi } from "../../redux/api/Call/EndCallSlice";
import { message, Modal } from "antd";
import { useState } from "react";
import EditSessionModal from "../NotesAndRecommendations/EditSessionModal";

export default function AppointmentDetails({
  appointmentId,
  detailData,
  myAppointment,
  detail,
  onExport,
  onSessionUpdated,
}) {
  const [modalVisible, setModalVisible] = useState(false);
  const [showSharedDocs, setShowSharedDocs] = useState(false);

  const handleEditSession = () => {
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
  };

  const formatTime = (timeString) => {
    if (!timeString) return ""; // return empty if null/undefined

    const [hours, minutes] = timeString.split(":");
    const date = new Date();
    date.setHours(hours, minutes);

    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };
  // Sample data - this would come from your API or props in a real app

  return (
    <div className="space-y-4 p-2 md:p-4 md:transform md:scale-[0.9] md:origin-top-left md:w-[111.11%] md:h-[111.11%]">
      {/* Upper Row - Appointment Details and Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Section - Appointment Details */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[16px] font-semibold text-[#171717] font-poppins">
              Appointment Details
            </h2>
            {(() => {
              // Completed/incomplete sessions, plus held sessions whose end-of-call form was never saved
              const isEditable = detailData?.status === "COMPLETED" || detailData?.status === "IN_COMPLETE"
                || detailData?.currentStatus === "RISK MISSING";

              return (
                <button
                  className="flex items-center gap-2 bg-[#F1F5F9] p-1 px-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: "transparent" }}
                  onClick={handleEditSession}
                  disabled={!isEditable}
                  title={isEditable ? "Edit session" : "Can only edit completed, incomplete or risk-missing appointments"}
                >
                  <span className="text-[14px] font-medium text-[#171717] font-poppins">Edit</span>
                  <Edit className="h-3.5 w-3.5 text-[#171717]" />
                </button>
              );
            })()}
          </div>

          <div className="space-y-2.5 bg-white border-[1px] border-solid border-[#E2E8F0] p-[14px] rounded-2xl shadow-sm">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                Appointment ID
              </span>
              <div className="flex items-center">
                <span className="flex items-center">
                  <span className="font-medium text-[14px] text-[#171717]">{detailData?.appointmentId}</span>
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                Mode
              </span>
              <div className="flex items-center">
                <span className="flex flex-row items-center gap-1.5 text-[#2563EB] text-[12px] font-semibold px-2.5 py-0.5 rounded-full bg-[#EFF6FF] font-poppins">
                  <img
                    src={getModeIcon(detailData?.mode)}
                    alt={"VIDEO"}
                    className="w-4 h-4 object-contain"
                  />{" "}
                  {detailData?.mode + " CALL"}
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                Status
              </span>
              <div className="flex items-center">
                <span className="flex items-center">
                  <span
                    className={`h-2 w-2 rounded-full ${getStatusColor(
                      detailData?.status,
                      detailData?.currentStatus
                    )} mr-2`}
                  ></span>
                  <span className="font-semibold text-[#2563EB] text-[14px]">{getStatusText(detailData?.status, detailData?.currentStatus)}</span>
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                Date
              </span>
              <span className="font-medium text-[14px] text-[#171717]">{detailData?.date}</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                Time
              </span>
              <span className="font-medium text-[14px] text-[#171717]">
                {formatTime(detailData?.startTime)} - {formatTime(detailData?.endTime)}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                Risk
              </span>
              <span
                className={`${getRiskColor(
                  detailData?.riskStatus || "N/A"
                )} px-2.5 py-0.5 font-semibold ${detailData?.riskStatus === "NO_RISK" ||
                    detailData?.riskStatus === "N/A" ||
                    detailData?.riskStatus === "LOW_RISK" ||
                    detailData?.riskStatus === null
                    ? "text-[#171717]"
                    : "text-[#FFFFFF]"
                  } rounded-full text-[12px]`}
              >
                {getStatusText(detailData?.riskStatus) || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Right Section - Basic Information */}
        <div>
          <h2 className="text-[16px] font-semibold mb-2 text-[#171717] font-poppins">
            Basic Information
          </h2>

          <div className="space-y-2.5 bg-white border-[1px] border-solid border-[#E2E8F0] p-[14px] rounded-2xl shadow-sm">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                Patient ID
              </span>
              <span className="text-[#4B61FF] text-[14px] font-medium font-poppins">
                {detail?.pcUserId || "N/A"}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                Patient Name
              </span>
              <span className="text-[#171717] text-[14px] font-medium font-poppins">
                {detail?.firstName || "N/A"} {detail?.lastName}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                Contact Number
              </span>
              <span className="text-[#171717] text-[14px] font-medium font-poppins">
                {detail?.mobile || "N/A"}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                Email Address
              </span>
              <span className="text-[#171717] text-[14px] font-medium font-poppins break-all sm:break-normal">
                {detail?.email || "N/A"}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                Gender
              </span>
              <span className="text-[#171717] text-[14px] font-medium font-poppins">
                {detail?.gender || "N/A"}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                DOB
              </span>
              <span className="text-[#171717] text-[14px] font-medium font-poppins">
                {detail?.dateOfBirth || "N/A"}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0">
              <span className="text-[#64748B] font-poppins text-[14px] font-normal">
                Groups
              </span>
              <span className="text-[#171717] text-[14px] font-medium font-poppins">
                {detail?.organisationName || "Phyber"}
              </span>
            </div>
          </div>

          {appointmentId && (
            <>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setShowSharedDocs(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setShowSharedDocs(true);
                }}
                className="mt-3 flex items-center gap-2.5 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm px-[14px] py-[12px] cursor-pointer hover:bg-[#F8FAFC] hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-center w-[32px] h-[32px] rounded-lg bg-[#EFF6FF] text-[#2563EB] flex-shrink-0">
                  <FolderOpen size={16} />
                </div>
                <div className="min-w-0">
                  <p className="text-[15px] font-semibold text-[#171717] font-poppins mb-0">
                    Shared documents
                  </p>
                  <p className="text-[13px] text-[#64748B] font-poppins mb-0">
                    View documents shared by the client
                  </p>
                </div>
                <ChevronRight size={16} className="ml-auto text-[#94A3B8] flex-shrink-0" />
              </div>

              <Modal
                open={showSharedDocs}
                onCancel={() => setShowSharedDocs(false)}
                footer={null}
                width={640}
                centered
                destroyOnClose
              >
                <div className="pt-6">
                  <SharedDocuments appointmentId={appointmentId} />
                </div>
              </Modal>
            </>
          )}
        </div>
      </div>

      {/* Lower Row - Session History and Attachments */}
      <div>
        <SessionHistory sessions={myAppointment} onExport={onExport} onSessionUpdated={onSessionUpdated} />
      </div>

      {/* Edit Session Modal */}
      <EditSessionModal
        visible={modalVisible}
        onClose={handleModalClose}
        sessionData={detailData}
        onSessionUpdated={onSessionUpdated}
      />
    </div>
  );
}
